import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { confirmSignUp, resendConfirmationCode } from '../utils/cognito';
import FocusGuard from './FocusGuard';
import PasswordToggle from './PasswordToggle';
import './Auth.css';

interface PasswordStrength {
  score: number; // 0–4
  label: string;
  color: string;
  missing: string[];
}

function getPasswordStrength(password: string): PasswordStrength {
  const missing: string[] = [];
  if (password.length < 8) missing.push('8+ characters');
  if (!/[A-Z]/.test(password)) missing.push('uppercase letter');
  if (!/[a-z]/.test(password)) missing.push('lowercase letter');
  if (!/\d/.test(password)) missing.push('number');
  if (!/[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/.test(password)) missing.push('special character');

  const score = Math.min(5 - missing.length, 4);
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#27ae60'];
  return { score, label: labels[score] ?? 'Very Weak', color: colors[score] ?? '#e74c3c', missing };
}

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Cognito requires email verification before the account is usable, so
  // registration flows into a confirm-code step rather than straight to login.
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');

  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await register(formData); // creates the Cognito user; emails a code
      setNeedsConfirm(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await confirmSignUp(formData.email, confirmCode.trim());
      // Account is verified — sign in with the password just chosen.
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirmation failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (needsConfirm) {
    return (
      <main id="main-content" className="auth-container">
        <FocusGuard landmarkId="confirm-form" />
        <div className="auth-card">
          <h2>Verify your email</h2>
          <p className="auth-subtext">Enter the code we emailed to {formData.email}.</p>
          <form onSubmit={handleConfirm} className="auth-form" id="confirm-form">
            <div className="form-group">
              <label htmlFor="confirm-code">Verification code</label>
              <input
                id="confirm-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={confirmCode}
                onChange={e => setConfirmCode(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            {error && <div id="confirm-error" className="error-message" role="alert" aria-live="assertive">{error}</div>}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? 'Verifying…' : 'Verify & continue'}
            </button>
          </form>
          <div className="auth-links">
            <button type="button" className="link-button" onClick={() => resendConfirmationCode(formData.email)}>
              Resend code
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" className="auth-container">
      <FocusGuard landmarkId="register-form" />
      <div className="auth-card">
        <h2>Register for AskYourCash</h2>

        <form onSubmit={handleSubmit} className="auth-form" id="register-form">
          <div className="form-group">
            <label htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name">Last Name</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={isLoading}
                minLength={8}
                aria-describedby={
                  // Only reference IDs that are actually in the DOM
                  [formData.password ? 'password-strength' : null, error ? 'register-error' : null]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
              />
              <PasswordToggle shown={showPassword} onToggle={() => setShowPassword(p => !p)} />
            </div>
            {formData.password && (() => {
              const strength = getPasswordStrength(formData.password);
              return (
                <div id="password-strength" className="password-strength" aria-live="polite">
                  <div className="password-strength-bar">
                    {[0,1,2,3,4].map(i => (
                      <div
                        key={i}
                        className="password-strength-segment"
                        style={{ backgroundColor: i < strength.score ? strength.color : '#3a3f4b' }}
                      />
                    ))}
                  </div>
                  <span className="password-strength-label" style={{ color: strength.color }}>
                    {strength.label}
                  </span>
                  {strength.missing.length > 0 && (
                    <span className="password-strength-hint">
                      Missing: {strength.missing.join(', ')}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {error && <div id="register-error" className="error-message" role="alert" aria-live="assertive">{error}</div>}

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <div className="auth-links">
          <p>
            Already have an account?{' '}
            <Link to="/login">Login here</Link>
          </p>
        </div>
      </div>
    </main>
  );
};

export default Register;